import { useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { ResponsiveDataList } from '../../../core/components/ui/ResponsiveDataList';
import { formatFileSize, formatUploader, formatDate } from '../../../core/components/ui/utils/formatters';
import { FileCard } from './FileCard';
import { FileTableActions } from './FileTableActions';

/**
 * File Table Component
 *
 * Displays files and folders using ResponsiveDataList with TanStack Table.
 * Shows table on desktop, cards on mobile.
 * Supports both files and folders with unified interface.
 *
 * @param {Object} props
 * @param {Array} props.files - Array of file/folder objects (can have type: 'file' | 'folder')
 * @param {Function} props.onDownload - Download handler (files only)
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onFolderClick - Folder click handler (optional)
 * @param {Function} props.onRename - Rename handler for folders (optional)
 * @param {Function} props.onMove - Move handler for files (optional)
 * @param {Object} props.dragAndDropHandlers - Drag and drop handlers (optional)
 * @param {string|null} props.downloadingFile - ID of file currently downloading
 *
 * @example
 * <FileTable
 *   files={items}
 *   onDownload={handleDownload}
 *   onDelete={handleDeleteClick}
 *   onFolderClick={handleFolderClick}
 *   downloadingFile={downloadingFile}
 * />
 */
export function FileTable({ files, onDownload, onDelete, onFolderClick, onRename, onMove, dragAndDropHandlers, downloadingFile }) {
  const { t } = useTranslation();

  // Use ref to access latest downloadingFile without recreating columns
  const downloadingFileRef = useRef(downloadingFile);

  useEffect(() => {
    downloadingFileRef.current = downloadingFile;
  }, [downloadingFile]);

  // Get row props for drag and drop
  const getRowProps = useCallback((row) => {
    const item = row.original;
    const isFile = item.type !== 'folder';
    const isFolder = item.type === 'folder';

    if (!dragAndDropHandlers) return {};

    const { handleDragStart, handleDragOver, handleDrop, handleDragEnd, draggingFileId } = dragAndDropHandlers;

    // Base props for all rows
    const props = {};

    // Files: draggable
    if (isFile) {
      props.draggable = true;
      props.onDragStart = (e) => handleDragStart(item, e);
      props.onDragEnd = handleDragEnd;
      // Add visual feedback when dragging - with white background
      if (draggingFileId === item.id) {
        props.className = 'opacity-60 bg-white';
      }
    }

    // Folders: drop targets
    if (isFolder) {
      props.onDragOver = (e) => handleDragOver(item, e);
      props.onDrop = (e) => handleDrop(item, e);
      // Add visual feedback when dragging over - highlight drop zone
      props.className = draggingFileId ? 'bg-brand/10 transition-colors' : '';
    }

    return props;
  }, [dragAndDropHandlers]);

  // Define table columns (TanStack Table format)
  // Wrap translation calls in functions to prevent unnecessary re-creation
  const columns = useMemo(() => [
    {
      accessorKey: 'original_filename',
      header: () => t('file.name', 'Name'),
      cell: (info) => {
        const item = info.row.original;
        const isFolder = item.type === 'folder';
        const name = isFolder ? item.name : item.original_filename;

        return (
          <div className="flex items-center gap-2">
            {isFolder && (
              <Icon icon="mdi:folder" className="w-5 h-5 text-brand flex-shrink-0" />
            )}
            <span className={`line-clamp-2 ${isFolder ? 'font-medium cursor-pointer' : 'text-default'}`}>
              {name}
            </span>
          </div>
        );
      },
      enableSorting: true,
      enableGlobalFilter: true,
    },
    {
      accessorKey: 'uploaded_by',
      header: () => t('file.uploader', 'Uploaded By'),
      cell: (info) => {
        const item = info.row.original;
        // For folders, show created_by; for files, show uploaded_by
        const user = item.type === 'folder' ? item.created_by : item.uploaded_by;
        return formatUploader(user, t);
      },
      enableSorting: true,
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'time_updated',
      header: () => t('files.explorer.lastModified', 'Fecha última modificación'),
      cell: (info) => {
        const item = info.row.original;
        // Use time_updated if available, otherwise fall back to time_created
        const date = item.time_updated || item.time_created;
        return formatDate(date);
      },
      enableSorting: true,
      enableGlobalFilter: false,
    },
    {
      id: 'actions',
      header: () => t('file.actions', 'Actions'),
      cell: ({ row }) => (
        <FileTableActions
          item={row.original}
          onDownload={() => onDownload(row.original)}
          onDelete={() => onDelete(row.original)}
          onRename={() => onRename && onRename(row.original)}
          onMove={() => onMove && onMove(row.original)}
          isDownloading={downloadingFileRef.current === row.original.id}
        />
      ),
    },
  ], [onDownload, onDelete, onRename, onMove]); // Removed 't' from dependencies

  // Mobile card renderer
  const renderMobileCard = useCallback((row) => (
    <FileCard
      file={row.original}
      onDownload={() => onDownload(row.original)}
      onDelete={() => onDelete(row.original)}
      onRename={() => onRename && onRename(row.original)}
      onMove={() => onMove && onMove(row.original)}
      isDownloading={downloadingFile === row.original.id}
    />
  ), [onDownload, onDelete, onRename, onMove, downloadingFile]);

  return (
    <ResponsiveDataList
      data={files}
      columns={columns}
      renderMobileItem={renderMobileCard}
      emptyMessage={t('file.noFiles', 'No files uploaded yet')}
      breakpoint="md"
      enableSorting={true}
      enableFiltering={false}
      getRowProps={getRowProps}
      onRowClick={(item) => {
        if (item.type === 'folder' && onFolderClick) {
          onFolderClick(item);
        } else if (item.type !== 'folder') {
          onDownload(item);
        }
      }}
    />
  );
}

FileTable.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['file', 'folder']),
      original_filename: PropTypes.string,
      name: PropTypes.string,
      content_type: PropTypes.string,
      file_size: PropTypes.number,
      file_count: PropTypes.number,
      time_created: PropTypes.string.isRequired,
      uploaded_by: PropTypes.object,
    })
  ).isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func,
  onRename: PropTypes.func,
  onMove: PropTypes.func,
  dragAndDropHandlers: PropTypes.shape({
    handleDragStart: PropTypes.func,
    handleDragOver: PropTypes.func,
    handleDrop: PropTypes.func,
    handleDragEnd: PropTypes.func,
    draggingFileId: PropTypes.string,
  }),
  downloadingFile: PropTypes.string,
};
